import { NavigateFunction } from 'react-router-dom';

import useBackendApi from '../../../../../hooks/useBackendApi';
import {
  AccountEventAssignedStaffResponse,
  AccountEventFormsResponse,
  EventClearanceStatusesResponse,
} from '../../../../../types/api/accountEvents';
import { EventRequirementFormOption, EventRequirementTestOption } from '../../../../../types/account';

const backendURL = process.env.REACT_APP_BACKEND_URL as string;

type ApiFetchRequest = ReturnType<typeof useBackendApi>['apiFetchRequest'];

type EventSupportFetchOptions = {
  accPathName: string;
  apiFetchRequest: ApiFetchRequest;
  eventId: number;
  fail: () => void;
  navigate: NavigateFunction;
  orgPathName: string;
};

type AccountEventFormsFetchOptions = Omit<EventSupportFetchOptions, 'eventId'> & {
  eventId?: number | null;
};

type RequirementOptionsFetchOptions = Omit<EventSupportFetchOptions, 'eventId'>;

type GuestClearanceReportDownloadResult =
  | { status: 'success'; blob: Blob }
  | { status: 'not-found' | 'failed' | 'unauthorized' };

function isUnauthorizedResponse(status: number, body: any): boolean {
  return (
    status === 401 ||
    status === 403 ||
    body?.status_code === 401 ||
    body?.message === 'UNAUTHENTICATED' ||
    body?.message === 'UNAUTHORIZED' ||
    body?.message === 'NO_ACCESS'
  );
}

export async function fetchEventClearanceStatuses({
  accPathName,
  apiFetchRequest,
  eventId,
  fail,
  navigate,
  orgPathName,
}: EventSupportFetchOptions): Promise<EventClearanceStatusesResponse | null> {
  const response = await apiFetchRequest({
    endpoint: `${backendURL}/o/${encodeURIComponent(orgPathName)}/a/${encodeURIComponent(
      accPathName,
    )}/events/${encodeURIComponent(String(eventId))}/clearance-statuses`,
    method: 'GET',
  });

  if (!response) {
    fail();
    return null;
  }

  const { body, status, isError } = response;
  if (isUnauthorizedResponse(status, body)) {
    navigate('/401.html');
    return null;
  }
  if (isNotFoundResponse(status, body)) {
    navigate('/404.html');
    return null;
  }
  if (isError || status !== 200 || !body || !Array.isArray(body.employees)) {
    fail();
    return null;
  }

  return body as EventClearanceStatusesResponse;
}

export async function checkEventClearanceReportAvailability({
  accPathName,
  apiFetchRequest,
  eventId,
  fail,
  navigate,
  orgPathName,
}: EventSupportFetchOptions): Promise<boolean | null> {
  const response = await apiFetchRequest({
    endpoint: `${backendURL}/o/${encodeURIComponent(orgPathName)}/a/${encodeURIComponent(
      accPathName,
    )}/events/${encodeURIComponent(String(eventId))}/clearance-report`,
    method: 'HEAD',
  });

  if (!response) {
    fail();
    return null;
  }
  if (isUnauthorizedResponse(response.status, response.body)) {
    navigate('/401.html');
    return null;
  }
  if (response.status === 404) {
    return false;
  }
  if (response.isError || response.status !== 200) {
    fail();
    return null;
  }

  return true;
}

export async function downloadEventClearanceReport({
  accPathName,
  apiFetchRequest,
  eventId,
  fail,
  navigate,
  orgPathName,
}: EventSupportFetchOptions): Promise<Blob | null> {
  const response = await apiFetchRequest({
    endpoint: `${backendURL}/o/${encodeURIComponent(orgPathName)}/a/${encodeURIComponent(
      accPathName,
    )}/events/${encodeURIComponent(String(eventId))}/clearance-report`,
    method: 'GET',
    isFile: true,
  });

  if (!response) {
    fail();
    return null;
  }
  if (isUnauthorizedResponse(response.status, response.body)) {
    navigate('/401.html');
    return null;
  }
  if (response.status === 404) {
    return null;
  }
  if (response.isError || response.status !== 200 || !(response.body instanceof Blob)) {
    fail();
    return null;
  }

  return response.body;
}

export async function downloadGuestClearanceReport({
  accPathName,
  apiFetchRequest,
  eventId,
  fail,
  navigate,
  orgPathName,
  guestUserId,
  clearanceStatus,
}: EventSupportFetchOptions & {
  guestUserId: string;
  clearanceStatus: string;
}): Promise<GuestClearanceReportDownloadResult> {
  const queryParams = new URLSearchParams({ clearance_status: clearanceStatus }).toString();
  const response = await apiFetchRequest({
    endpoint: `${backendURL}/o/${encodeURIComponent(orgPathName)}/a/${encodeURIComponent(
      accPathName,
    )}/events/${encodeURIComponent(String(eventId))}/guests/${encodeURIComponent(guestUserId)}/clearance-report?${queryParams}`,
    method: 'GET',
    isFile: true,
  });

  if (!response) {
    fail();
    return { status: 'failed' };
  }
  if (isUnauthorizedResponse(response.status, response.body)) {
    navigate('/401.html');
    return { status: 'unauthorized' };
  }
  if (response.status === 404) {
    return { status: 'not-found' };
  }
  if (response.isError || response.status !== 200 || !(response.body instanceof Blob)) {
    fail();
    return { status: 'failed' };
  }

  return { status: 'success', blob: response.body };
}

function isNotFoundResponse(status: number, body: any): boolean {
  return status === 404 || body?.status_code === 404 || body?.message === 'NOT_FOUND';
}

export async function fetchAccountEventAssignedStaff({
  accPathName,
  apiFetchRequest,
  eventId,
  fail,
  navigate,
  orgPathName,
}: EventSupportFetchOptions): Promise<AccountEventAssignedStaffResponse['assigned_staff'] | null> {
  const response = await apiFetchRequest({
    endpoint: `${backendURL}/o/${encodeURIComponent(orgPathName)}/a/${encodeURIComponent(
      accPathName,
    )}/events/${encodeURIComponent(String(eventId))}/staff`,
    method: 'GET',
  });

  if (!response) {
    fail();
    return null;
  }

  const { body, status, isError } = response;
  const typedBody = body as AccountEventAssignedStaffResponse | undefined;

  if (isUnauthorizedResponse(status, body)) {
    navigate('/401.html');
    return null;
  }

  if (isNotFoundResponse(status, body)) {
    navigate('/404.html');
    return null;
  }

  if (isError || status !== 200 || !typedBody) {
    fail();
    return null;
  }

  return typedBody.assigned_staff;
}

export async function fetchAccountEventForms({
  accPathName,
  apiFetchRequest,
  eventId = null,
  fail,
  navigate,
  orgPathName,
}: AccountEventFormsFetchOptions): Promise<AccountEventFormsResponse['linked_forms'] | null> {
  const eventPath = eventId !== null && eventId !== undefined ? `${encodeURIComponent(String(eventId))}/` : '';
  const response = await apiFetchRequest({
    endpoint: `${backendURL}/o/${encodeURIComponent(orgPathName)}/a/${encodeURIComponent(
      accPathName,
    )}/events/${eventPath}forms`,
    method: 'GET',
  });

  if (!response) {
    fail();
    return null;
  }

  const { body, status, isError } = response;
  const typedBody = body as AccountEventFormsResponse | undefined;

  if (isUnauthorizedResponse(status, body)) {
    navigate('/401.html');
    return null;
  }

  if (isNotFoundResponse(status, body)) {
    navigate('/404.html');
    return null;
  }

  if (isError || status !== 200 || !typedBody || !Array.isArray(typedBody.linked_forms)) {
    fail();
    return null;
  }

  return typedBody.linked_forms;
}

export async function fetchEventRequirementForms({
  accPathName,
  apiFetchRequest,
  fail,
  navigate,
  orgPathName,
}: RequirementOptionsFetchOptions): Promise<EventRequirementFormOption[] | null> {
  const response = await apiFetchRequest({
    endpoint: `${backendURL}/o/${encodeURIComponent(orgPathName)}/a/${encodeURIComponent(
      accPathName,
    )}/events/requirement-forms`,
    method: 'GET',
  });

  if (!response) {
    fail();
    return null;
  }

  const { body, status, isError } = response;

  if (isUnauthorizedResponse(status, body)) {
    navigate('/401.html');
    return null;
  }

  if (isNotFoundResponse(status, body)) {
    navigate('/404.html');
    return null;
  }

  if (isError || status !== 200 || !Array.isArray(body)) {
    fail();
    return null;
  }

  return body as EventRequirementFormOption[];
}

export async function fetchEventRequirementTests({
  accPathName,
  apiFetchRequest,
  fail,
  navigate,
  orgPathName,
}: RequirementOptionsFetchOptions): Promise<EventRequirementTestOption[] | null> {
  const response = await apiFetchRequest({
    endpoint: `${backendURL}/o/${encodeURIComponent(orgPathName)}/a/${encodeURIComponent(accPathName)}/events/tests`,
    method: 'GET',
  });

  if (!response) {
    fail();
    return null;
  }

  const { body, status, isError } = response;

  if (isUnauthorizedResponse(status, body)) {
    navigate('/401.html');
    return null;
  }

  if (isNotFoundResponse(status, body)) {
    navigate('/404.html');
    return null;
  }

  if (isError || status !== 200 || !Array.isArray(body)) {
    fail();
    return null;
  }

  return body as EventRequirementTestOption[];
}

export type BillingServiceKey =
  | 'RESPIRATOR_MEDICAL'
  | 'FIT_TEST'
  | 'PULMONARY_FUNCTION_TEST'
  | 'AUDIOMETRY';

export interface RequestedServiceItem {
  billing_service_key?: BillingServiceKey | null;
  name: string;
  requested_count: number;
  done_count: number;
}

export interface ServiceRequestsMetadata {
  services_requested: RequestedServiceItem[];
}

export interface AccountEventMetadataResponse {
  meta_value: ServiceRequestsMetadata;
}

interface AccountEventMetadataUpdateResponse {
  status: 'SUCCESS';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isRequestedServiceItem(value: unknown): value is RequestedServiceItem {
  const billingServiceKey = isRecord(value) ? value.billing_service_key : undefined;
  return (
    isRecord(value) &&
    (billingServiceKey === undefined ||
      billingServiceKey === null ||
      billingServiceKey === 'RESPIRATOR_MEDICAL' ||
      billingServiceKey === 'FIT_TEST' ||
      billingServiceKey === 'PULMONARY_FUNCTION_TEST' ||
      billingServiceKey === 'AUDIOMETRY') &&
    typeof value.name === 'string' &&
    Number.isInteger(value.requested_count) &&
    Number.isInteger(value.done_count)
  );
}

function isAccountEventMetadataResponse(value: unknown): value is AccountEventMetadataResponse {
  return (
    isRecord(value) &&
    isRecord(value.meta_value) &&
    Array.isArray(value.meta_value.services_requested) &&
    value.meta_value.services_requested.every(isRequestedServiceItem)
  );
}

function isAccountEventMetadataUpdateResponse(value: unknown): value is AccountEventMetadataUpdateResponse {
  return isRecord(value) && value.status === 'SUCCESS';
}

export async function fetchAccountEventMetadata({
  orgPathName,
  accPathName,
  eventId,
  metaKey,
  apiFetchRequest,
  fail,
  navigate,
}: EventSupportFetchOptions & { metaKey: string }): Promise<AccountEventMetadataResponse | null> {
  const response = await apiFetchRequest({
    endpoint: `${backendURL}/o/${encodeURIComponent(orgPathName)}/a/${encodeURIComponent(accPathName)}/events/${eventId}/metadata/${encodeURIComponent(metaKey)}`,
    method: 'GET',
  });

  if (!response) {
    fail();
    return null;
  }

  const { body, status, isError } = response;

  if (isUnauthorizedResponse(status, body)) {
    navigate('/401.html');
    return null;
  }

  if (isError || status !== 200 || !isAccountEventMetadataResponse(body)) {
    fail();
    return null;
  }

  return body;
}

export async function updateAccountEventMetadata({
  orgPathName,
  accPathName,
  eventId,
  metaKey,
  metaValue,
  apiFetchRequest,
  fail,
  navigate,
}: EventSupportFetchOptions & {
  metaKey: string;
  metaValue: ServiceRequestsMetadata;
}): Promise<boolean> {
  const response = await apiFetchRequest({
    endpoint: `${backendURL}/o/${encodeURIComponent(orgPathName)}/a/${encodeURIComponent(accPathName)}/events/${eventId}/metadata/${encodeURIComponent(metaKey)}`,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: { services_requested: metaValue.services_requested },
  });

  if (!response) {
    fail();
    return false;
  }

  const { body, status, isError } = response;

  if (isUnauthorizedResponse(status, body)) {
    navigate('/401.html');
    return false;
  }

  if (isError || status !== 200 || !isAccountEventMetadataUpdateResponse(body)) {
    fail();
    return false;
  }

  return true;
}
